import { X, Trash2, ShoppingCart, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useImportCartStore } from '../../stores/import-cart.store';
import { useImportOpportunities } from '../../hooks/useBoampSearch';
import { toast } from 'sonner';

interface ImportCartProps {
  onImportSuccess?: () => void;
}

export function ImportCart({ onImportSuccess }: ImportCartProps) {
  const { items, isOpen, closeCart, removeItem, clearCart } =
    useImportCartStore();
  const importMutation = useImportOpportunities();

  const handleImport = async () => {
    if (items.length === 0) return;

    try {
      const result = await importMutation.mutateAsync(
        items.map((item) => ({
          externalId: item.externalId,
          source: item.source,
        }))
      );

      // Afficher les résultats
      if (result.successCount > 0) {
        toast.success(`${result.successCount} opportunité(s) importée(s) avec succès`);
      }
      if (result.alreadyExistsCount > 0) {
        toast.info(`${result.alreadyExistsCount} opportunité(s) déjà existante(s)`);
      }
      if (result.errorCount > 0) {
        toast.error(`${result.errorCount} erreur(s) lors de l'import`);
      }

      // Vider le panier si tout est importé
      if (result.errorCount === 0) {
        clearCart();
      } else {
        // Retirer uniquement les imports réussis
        result.items
          .filter((item) => item.status === 'success')
          .forEach((item) => removeItem(item.externalId));
      }

      onImportSuccess?.();
    } catch (error) {
      toast.error("Erreur lors de l'import des opportunités");
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={closeCart}
      />

      {/* Slide-in panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-surface shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-copper" />
            <h2 className="font-semibold">Panier d'import</h2>
            <Badge variant="secondary">{items.length}</Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={closeCart}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Votre panier est vide</p>
              <p className="text-sm mt-1">
                Ajoutez des opportunités depuis les résultats de recherche
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.externalId}
                  className="bg-muted/50 rounded-lg p-3 group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium line-clamp-2">
                        {item.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs px-1.5">
                          {item.source.toUpperCase()}
                        </Badge>
                        <span>{item.externalId}</span>
                      </div>
                      {item.submissionDeadline && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Limite: {formatDate(item.submissionDeadline)}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                      onClick={() => removeItem(item.externalId)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="p-4 border-t space-y-2">
            <Button
              className="w-full bg-copper hover:bg-copper/90"
              onClick={handleImport}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Importer {items.length} opportunité
                  {items.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={clearCart}
              disabled={importMutation.isPending}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Vider le panier
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
